import { mapCellValues, toCamelCase } from './visio-core';
import { isValidColor } from './visio-theme';
/**
 * Retrieves a specific Visio Section from a shape by name.
 * Useful for accessing rich text-related sections like 'Character' or 'Paragraph'.
 *
 * @param {VisioShapeNode} shape - The Visio shape object that may contain Section elements
 * @param {string} name - The Section name to find (e.g., 'Character', 'Paragraph')
 * @returns {VisioSection | null} The matched Section object if found; otherwise null
 */
function getSection(shape, name) {
    if (!shape || !shape.Section) {
        return null;
    }
    const sections = Array.isArray(shape.Section) ? shape.Section : [shape.Section];
    return sections.find((section) => section && section.$ && section.$.N === name) || null;
}
/**
 * Ensures a value is returned as an array.
 * If the value is already an array, it is returned unchanged; if non-null, it is wrapped in an array; otherwise returns an empty array.
 *
 * @template T
 * @param {any} v - The input value that may be an array or single item
 * @returns {T[]} An array wrapping the input, or empty array when input is null/undefined
 */
function ensureArray(v) { return Array.isArray(v) ? v : (v != null ? [v] : []); }
/**
 * Determines whether a shape's text has mixed character formatting across Character rows.
 * Mixed formatting implies the presence of multiple style signatures (font, size, style, color)
 * and multiple word tokens or multiple paragraph rows�indicating rich text that a single EJ2
 * annotation style cannot faithfully reproduce.
 *
 * @param {VisioShapeNode} shape - Visio shape object with potential 'Character' and 'Paragraph' sections
 * @returns {boolean} True if mixed character formatting is detected; otherwise false
 */
function hasMixedCharacterFormatting(shape) {
    const characterSection = getSection(shape, 'Character');
    if (!characterSection || !characterSection.Row) {
        return false;
    }
    // Sets to collect distinct Font/Size/Color values (size kept as string per requirement)
    const distinctFonts = new Set();
    const distinctSizes = new Set();
    const distinctColors = new Set();
    // Unique style signatures across character rows
    const characterRows = ensureArray(characterSection.Row);
    for (const row of characterRows) {
        const cells = ensureArray(row.Cell);
        let isFontValueFound;
        let isSizeValueFound;
        let isColorValueFound;
        for (const cell of cells) {
            if (!cell || !cell.$ || !cell.$.N) {
                continue;
            }
            const cellName = cell.$.N;
            const cellValue = cell.$.V;
            if (cellName === 'Font' && cellValue) {
                distinctFonts.add(cellValue);
                isFontValueFound = true;
            }
            if (cellName === 'Size' && cellValue) {
                const size = Number(cellValue);
                // Normalize numeric precision to avoid tiny float differences
                distinctSizes.add(String(Math.round(size * 1000) / 1000));
                isSizeValueFound = true;
            }
            if (cellName === 'Color' && cellValue) {
                distinctColors.add(cellValue);
                isColorValueFound = true;
            }
            // Stop early when all three values discovered for this row
            if (isFontValueFound && isSizeValueFound && isColorValueFound) {
                break;
            }
        }
    }
    // Only consider mixed when font OR size OR color actually vary across rows
    const mixedStyleDetected = (distinctFonts.size > 1) || (distinctSizes.size > 1) || (distinctColors.size > 1);
    const raw = shape && shape.Text && shape.Text.value ? String(shape.Text.value) : '';
    // Remove tags but keep whitespace to assess words
    const plain = raw.replace(/<cp[^>]*\/>/gi, ' ').replace(/<pp[^>]*\/>/gi, ' ').replace(/\s+/g, ' ').trim();
    const hasMultiWord = /\S\s+\S/.test(plain);
    // Paragraph info � if there are many paragraphs, treat as multi-part text even if single token
    const paraSec = getSection(shape, 'Paragraph');
    const paraRows = paraSec && paraSec.Row ? ensureArray(paraSec.Row).length : 0;
    // Mixed only mixed style detected AND (multiple words OR multiple paragraphs)
    return mixedStyleDetected && (hasMultiWord || paraRows > 1);
}
/**
 * Normalizes Visio rich-text content for EJ2 by converting internal paragraph markers
 * into actual line breaks. If the input contains multiple Paragraph rows but no newline
 * characters, it heuristically inserts line breaks before list/bullet patterns.
 *
 * @param {any} shape - Visio shape object used to inspect the 'Paragraph' section
 * @param {string} content - Raw text content possibly containing Visio paragraph markers
 * @returns {string} Normalized text with line breaks suitable for EJ2 annotations
 */
function normalizeParagraphBreaks(shape, content) {
    if (!content) {
        return content;
    }
    // Replace explicit paragraph markers if the raw text still contains them
    let text = content.replace(/<cp[^>]*\/>/gi, '')
        .replace(/<pp[^>]*\/>/gi, '\n');
    // If there are no newlines but Paragraph section indicates multiple paragraphs,
    // heuristically split after period-number bullets and bullet glyphs.
    if (!/\n/.test(text)) {
        const paraSec = getSection(shape, 'Paragraph');
        const paraRows = paraSec && paraSec.Row ? ensureArray(paraSec.Row) : [];
        if (paraRows.length > 1) {
            // Insert newline before numbered list items (2. , 3. , etc.) that are not at start
            text = text.replace(/\s+(?=\d+\.)/g, '\n');
            // Insert newline before bullet glyphs � or hyphen bullets
            text = text.replace(/\s+(?=[�-]\s)/g, '\n');
        }
    }
    return text;
}
/**
 * Applies a conservative default text style when mixed rich-text runs are detected
 * and a single EJ2 annotation style cannot represent the variation.
 * Resets bold/italic, sets a readable default font family and size, and clears decorations.
 *
 * @param {VisioTextStyleModel} style - The current text style model to adjust
 * @returns {VisioTextStyleModel} The updated style model with safe default settings applied
 */
function applyDefaultStyleForMixedRuns(style) {
    if (!style) {
        return style;
    }
    style.bold = false;
    style.italic = false;
    // Default family
    style.fontFamily = 'Segoe UI';
    // Slightly increased default font size for readability
    style.fontSize = 16; // pixels
    // Remove decorations
    if (style.textDecoration) {
        style.textDecoration.underline = false;
        style.textDecoration.strikethrough = false;
    }
    return style;
}
/**
 * Represents margin properties for Visio shapes.
 * Defines spacing on all four sides (left, right, top, bottom).
 */
export class VisioMarginModel {
    constructor() {
        /** Margin on the left side */
        this.left = 0;
        /** Margin on the right side */
        this.right = 0;
        /** Margin on the top side */
        this.top = 0;
        /** Margin on the bottom side */
        this.bottom = 0;
    }
    /**
     * Creates a VisioMarginModel instance from a Visio shape object.
     * Extracts margin values from the shape's Cell elements.
     * @param {any} shape - The Visio shape object containing Cell elements with margin data
     * @returns {VisioMarginModel} A new VisioMarginModel with extracted margin values, or default values if shape is invalid
     */
    static fromJs(shape) {
        const margin = new VisioMarginModel();
        // Validate shape and its properties
        if (!shape || !shape.$) {
            return margin;
        }
        /**
         * Helper function to retrieve a cell value by name
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value, or undefined if not found
         */
        const getCell = (name) => {
            // Ensure shape.Cell is always an array
            const cells = ensureArray(shape.Cell);
            const cell = cells.find((c) => c.$.N === name);
            return cell ? cell.$.V : undefined;
        };
        // Extract margin values from cells and convert to numbers
        margin.left = Number(getCell('LeftMargin')) || 0;
        margin.right = Number(getCell('RightMargin')) || 0;
        margin.top = Number(getCell('TopMargin')) || 0;
        margin.bottom = Number(getCell('BottomMargin')) || 0;
        return margin;
    }
}
/**
 * Represents text decoration properties for Visio text elements.
 * Handles underline and strikethrough styling.
 */
export class VisioTextDecorationModel {
    constructor() {
        /** Flag indicating if text is underlined */
        this.underline = false;
        /** Flag indicating if text has strikethrough */
        this.strikethrough = false;
    }
    /**
     * Creates a VisioTextDecorationModel instance from a Visio shape object.
     * Extracts text decoration properties from shape cells using bitwise operations on the Style cell.
     * @param {any} shape - The Visio shape object containing Section elements with decoration data
     * @returns {VisioTextDecorationModel} A new VisioTextDecorationModel with extracted decoration values
     */
    static fromJs(shape) {
        const style = new VisioTextDecorationModel();
        const allCells = [];
        // Validate shape
        if (!shape || !shape.$) {
            return style;
        }
        // Normalize Section to always be an array
        const sections = Array.isArray(shape.Section) ? shape.Section : [shape.Section];
        // Iterate through all sections and rows to collect cells
        for (const section of sections) {
            const rows = Array.isArray(section.Row) ? section.Row : [section.Row];
            for (const row of rows) {
                // Skip invalid rows
                if (!row || typeof row !== 'object') {
                    continue;
                }
                const cells = Array.isArray(row.Cell) ? row.Cell : [row.Cell];
                allCells.push(...cells);
            }
        }
        /**
         * Helper function to find a cell by name across all collected cells
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value, or undefined if not found
         */
        const getCell = (name) => {
            const cell = allCells.find((c) => c && c.$ && c.$.N === name);
            return (cell && cell.$ && cell.$.V);
        };
        // Extract and parse style code (bitwise flags for text styling)
        const styleCode = parseInt(getCell('Style'), 10);
        // Check strikethrough flag (1 = strikethrough)
        const strikethrough = Number(getCell('Strikethru'));
        if (strikethrough === 1) {
            style.strikethrough = true;
        }
        // Use bitwise AND operation to check if underline bit (4) is set
        if ((styleCode)) {
            style.underline = (styleCode & 4) !== 0;
        }
        return style;
    }
}
/**
 * Represents text alignment properties for Visio text elements.
 * Handles horizontal alignment (left, center, right, justify).
 */
export class VisioTextAlignmentModel {
    constructor() {
        /** Flag indicating left alignment */
        this.left = false;
        /** Flag indicating right alignment */
        this.right = false;
        /** Flag indicating center alignment (default) */
        this.center = true;
        /** Flag indicating justified alignment */
        this.justify = false;
    }
    /**
     * Creates a VisioTextAlignmentModel instance from a Visio shape object.
     * Extracts horizontal alignment from the HorzAlign cell value.
     * @param {any} shape - The Visio shape object containing alignment data
     * @returns {VisioTextAlignmentModel} A new VisioTextAlignmentModel with extracted alignment values
     */
    static fromJs(shape) {
        const alignment = new VisioTextAlignmentModel();
        const allCells = [];
        // Validate shape
        if (!shape || !shape.$) {
            return alignment;
        }
        // Normalize Section to always be an array
        const sections = Array.isArray(shape.Section) ? shape.Section : [shape.Section];
        // Collect all cells from sections and rows
        for (const section of sections) {
            const rows = Array.isArray(section.Row) ? section.Row : [section.Row];
            for (const row of rows) {
                if (!row || typeof row !== 'object') {
                    continue;
                }
                const cells = Array.isArray(row.Cell) ? row.Cell : [row.Cell];
                allCells.push(...cells);
            }
        }
        /**
         * Helper function to find a cell by name
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value, or undefined if not found
         */
        const getCell = (name) => {
            const cell = allCells.find((c) => c && c.$ && c.$.N === name);
            return (cell && cell.$ && cell.$.V);
        };
        // Get horizontal alignment value from HorzAlign cell
        const alignValue = getCell('HorzAlign');
        // Map alignment codes to boolean flags
        // 0 = left, 2 = right, 3 = justify, default = center
        switch (alignValue) {
            case '0':
                alignment.left = true;
                break;
            case '2':
                alignment.right = true;
                break;
            case '3':
                alignment.justify = true;
                break;
            default:
                alignment.center = true;
        }
        return alignment;
    }
}
/**
 * Represents comprehensive text styling properties for Visio text elements.
 * Includes color, font, size, bold, italic, decoration, and alignment.
 */
export class VisioTextStyleModel {
    constructor() {
        /** Font family name (default: 'Calibri') */
        this.fontFamily = 'Calibri';
        /** Flag indicating if text is italic */
        this.italic = false;
        /** Flag indicating if text is bold */
        this.bold = false;
        /** Flag indicating no text styling applied */
        this.TEXT_STYLE_NONE = true;
        /** Text opacity (0-1 range) */
        this.opacity = 1;
    }
    /**
     * Creates a VisioTextStyleModel instance from a Visio shape object.
     * Extracts comprehensive text styling information including font, size, color, and decorations.
     * @param {any} shape - The Visio shape object containing text style data
     * @param {boolean} isConnector - To Check whether the shape is connector or not
     * @returns {VisioTextStyleModel} A new VisioTextStyleModel with all extracted style properties
     */
    static fromJs(shape, isConnector) {
        const style = new VisioTextStyleModel();
        const allCells = [];
        // Validate shape has Section property
        if (!shape || !shape.Section) {
            return style;
        }
        // Normalize Section to always be an array
        const sections = Array.isArray(shape.Section) ? shape.Section : [shape.Section];
        // Collect all cells from sections and rows
        for (const section of sections) {
            const rows = Array.isArray(section.Row) ? section.Row : [section.Row];
            for (const row of rows) {
                if (!row || typeof row !== 'object') {
                    continue;
                }
                const cells = Array.isArray(row.Cell) ? row.Cell : [row.Cell];
                allCells.push(...cells);
            }
        }
        /**
         * Helper function to find a cell by name in Section cells
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value
         */
        const getCell = (name) => {
            const cell = allCells.find((c) => c && c.$ && c.$.N === name);
            return (cell && cell.$ && cell.$.V);
        };
        /**
         * Helper function to find a cell by name in direct Cell array
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value
         */
        const getCells = (name) => {
            // Ensure shape.Cell is always an array
            const cells = ensureArray(shape.Cell);
            const cell = cells.find((c) => c.$.N === name);
            return cell ? cell.$.V : undefined;
        };
        /**
         * Helper function to get cell unit property
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell unit value
         */
        const getCellUnit = (name) => {
            const cell = allCells.find((c) => c && c.$ && c.$.N === name);
            return cell && cell.$ && cell.$.U;
        };
        // Extract color value
        style.color = getCell('Color') != null ? getCell('Color') : undefined;
        // Extract and set font family
        style.fontFamily = getCell('Font') ? getCell('Font') : 'Calibri';
        // Extract and convert font size (multiply by 72 * 1.33 to get point size)
        const fontSizeValue = Number(getCell('Size'));
        if (fontSizeValue) {
            style.fontSize = fontSizeValue * 72 * 1.33;
        }
        // Extract style code and use bitwise operations to determine bold and italic
        // Bit 0 (1) = bold, Bit 1 (2) = italic
        const styleCode = parseInt(getCell('Style'), 10);
        if (styleCode > -1) {
            style.bold = (styleCode & 1) !== 0;
            style.italic = (styleCode & 2) !== 0;
            style.TEXT_STYLE_NONE = false;
        }
        // Extract opacity/transparency
        style.opacity = Number(getCell('ColorTrans')) || 1;
        // Extract text alignment properties
        style.textAlign = VisioTextAlignmentModel.fromJs(shape);
        // Extract background color
        const textBkgnd = getCells('TextBkgnd');
        const fill = textBkgnd !== undefined && textBkgnd !== null && textBkgnd !== ''
            ? (textBkgnd === '0' ? 'transparent' : textBkgnd)
            : (isConnector ? 'white' : 'transparent');
        style.fill = isValidColor(fill, true) ? fill : 'transparent';
        // Extract text decoration properties
        style.textDecoration = VisioTextDecorationModel.fromJs(shape);
        return style;
    }
}
/**
 * Represents hyperlink properties for Visio shapes.
 * Stores link address, description, and target window preferences.
 */
export class VisioHyperlinkModel {
    constructor() {
        /** The hyperlink URL/address */
        this.link = '';
        /** The hyperlink display content/description */
        this.content = '';
        /** Flag indicating if link opens in a new window */
        this.newWindow = false;
    }
    /**
     * Creates a VisioHyperlinkModel instance from a Visio shape object.
     * Extracts hyperlink data from the Hyperlink section of the shape.
     * @param {any} shape - The Visio shape object containing hyperlink data
     * @returns {VisioHyperlinkModel} A new VisioHyperlinkModel with extracted hyperlink properties
     */
    static fromJs(shape) {
        // Validate shape and Section property
        if (!shape || !shape.Section) {
            return new VisioHyperlinkModel();
        }
        // Find the Hyperlink section (may be array or single object)
        let hyperlinkSection = null;
        if (Array.isArray(shape.Section)) {
            hyperlinkSection = shape.Section.find((sec) => sec.$.N === 'Hyperlink');
        }
        else if (shape.Section) {
            if (shape.Section.$.N === 'Hyperlink') {
                hyperlinkSection = shape.Section;
            }
        }
        // Validate hyperlink section and rows
        if (!hyperlinkSection || !hyperlinkSection.Row) {
            return new VisioHyperlinkModel();
        }
        // Normalize rows to always be an array
        const rows = Array.isArray(hyperlinkSection.Row) ? hyperlinkSection.Row : [hyperlinkSection.Row];
        const row = rows[0]; // Just get the first row
        /**
         * Helper function to get cell value by name from the hyperlink row
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value
         */
        const getCell = (name) => {
            const cell = (row.Cell || []).find((c) => c.$.N === name);
            return cell ? cell.$.V : undefined;
        };
        // Create and populate hyperlink model
        const hyperlink = new VisioHyperlinkModel();
        hyperlink.content = getCell('Description');
        hyperlink.link = getCell('Address');
        // Extract NewWindow flag (1 = true)
        const newWindow = Number(getCell('NewWindow'));
        if (newWindow === 1) {
            hyperlink.newWindow = true;
        }
        return hyperlink;
    }
}
/**
 * Utility class for binding Visio text properties to Syncfusion text binding format.
 * Handles text positioning calculations and coordinate conversions.
 */
export class VisioToSyncfusionTextBinder {
    /**
     * Binds Visio text transform properties to Syncfusion text binding format.
     * Calculates the offset position of text within a shape using Visio pin and margin data.
     * @param {VisioShapeTransform} shapeTransform - The Visio shape transform data (position, size)
     * @param {VisioTextTransform} textTransform - The Visio text transform data (pins, margins, dimensions)
     * @returns {SyncfusionTextBinding} A Syncfusion text binding with calculated offset (0-1 normalized range)
     */
    static bindVisioTextToSyncfusion(shapeTransform, textTransform) {
        // Step 1: Calculate base position offset from TxtPin coordinates
        const visioTextCenter = this.calculateVisioTextCenter(shapeTransform, textTransform);
        const positionOffset = this.convertToSyncfusionOffset(visioTextCenter, shapeTransform);
        const combinedOffset = {
            x: positionOffset.x,
            y: positionOffset.y
        };
        if (shapeTransform.verticalAlignment === 'Bottom') {
            combinedOffset.y += 0.2;
        }
        else if (shapeTransform.verticalAlignment === 'Top') {
            combinedOffset.y -= 0.2;
        }
        // Return binding with rounded offset values to 2 decimal places
        return {
            offset: {
                x: Math.round(combinedOffset.x * 100) / 100,
                y: Math.round(combinedOffset.y * 100) / 100
            }
        };
    }
    /**
     * Calculates the absolute center position of text within a Visio shape.
     * Takes into account text pin position, margins, and dimensions.
     * @param {VisioShapeTransform} shapeTransform - The shape transform data (pinX, pinY, width, height)
     * @param {VisioTextTransform} textTransform - The text transform data (pins, margins, dimensions)
     * @returns {{ x: number, y: number }} The absolute center position of the text
     */
    static calculateVisioTextCenter(shapeTransform, textTransform) {
        // Calculate shape's bottom-left corner position
        const shapeLeft = shapeTransform.pinX - shapeTransform.width / 2;
        const shapeBottom = shapeTransform.pinY - shapeTransform.height / 2;
        // Calculate absolute text pin position including margin offsets
        const absoluteTxtPinX = shapeLeft + textTransform.txtPinX + (textTransform.txtMargin.left - textTransform.txtMargin.right);
        const absoluteTxtPinY = shapeBottom + textTransform.txtPinY +
            (textTransform.txtMargin.bottom - textTransform.txtMargin.top);
        // Calculate offset from text pin to text center
        const textCenterOffsetX = (textTransform.txtWidth / 2) - textTransform.txtLocPinX;
        const textCenterOffsetY = (textTransform.txtHeight / 2) - textTransform.txtLocPinY;
        // Apply rotation transformations to offset (placeholder for future rotation support)
        const rotatedOffsetX = textCenterOffsetX;
        const rotatedOffsetY = textCenterOffsetY;
        // Calculate final text center position
        const visioTextCenterX = absoluteTxtPinX + rotatedOffsetX;
        const visioTextCenterY = absoluteTxtPinY + rotatedOffsetY;
        return {
            x: visioTextCenterX,
            y: visioTextCenterY
        };
    }
    /**
     * Converts Visio absolute text center coordinates to Syncfusion normalized offset (0-1 range).
     * Normalizes position relative to shape bounds.
     * @param {{ x: number, y: number }} visioTextCenter - The absolute text center position
     * @param {VisioShapeTransform} shapeTransform - The shape transform data for normalization
     * @returns {{ x: number, y: number }} Normalized offset in 0-1 range where 0.5 is center
     */
    static convertToSyncfusionOffset(visioTextCenter, shapeTransform) {
        // Get shape center coordinates
        const shapeCenterX = shapeTransform.pinX;
        const shapeCenterY = shapeTransform.pinY;
        // Normalize text position relative to shape
        // X offset: 0.5 + (text position - shape center) / shape width
        const offsetX = 0.5 + (visioTextCenter.x - shapeCenterX) / shapeTransform.width;
        // Y offset: 0.5 - (text position - shape center) / shape height
        // Note: Y is inverted (Visio Y increases upward, Syncfusion increases downward)
        const offsetY = 0.5 - (visioTextCenter.y - shapeCenterY) / shapeTransform.height;
        return {
            x: offsetX,
            y: offsetY
        };
    }
}
/**
 * Represents a text annotation (label) for Visio shapes and connectors.
 * Contains text content, styling, positioning, and visibility information.
 */
export class VisioAnnotation {
    constructor() {
        /** Text content of the annotation */
        this.content = '';
        /** Rotation angle in degrees */
        this.rotateAngle = 0;
        /** Flag indicating if annotation is visible */
        this.visible = true;
        /** Vertical alignment of text (Top, Center, Bottom) */
        this.verticalAlignment = 'Center';
        /** Horizontal alignment of text (Left, Center, Right) */
        this.horizontalAlignment = 'Center';
        /** Flag indicating if text angle should follow connector segment angle */
        this.segmentAngle = false;
    }
}
/**
 * Determines shape-specific text transform properties based on shape type.
 * Different shapes require different text positioning to maintain proper alignment.
 * @function applyTextTransform
 * @param {string} shapeName - The name/type of the shape (rectangle, ellipse, etc.)
 * @param {number} shapeWidth - The width of the shape
 * @param {number} shapeHeight - The height of the shape
 * @returns {{
 *   txtWidth: number,
 *   txtHeight: number,
 *   txtPinX: number,
 *   txtPinY: number,
 *   txtLocPinX: number,
 *   txtLocPinY: number
 * }} Text transform properties customized for the shape type
 */
function applyTextTransform(shapeName, shapeWidth, shapeHeight) {
    // Normalize shape name for comparison
    const safeName = (shapeName && shapeName.trim().length) ? shapeName : 'shape';
    // Return shape-specific text positioning multipliers
    switch (safeName.toLowerCase()) {
        case 'rectangle':
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'ellipse':
            // Ellipse uses 87.5% of shape dimensions for text
            return {
                txtWidth: shapeWidth * 0.875, txtHeight: shapeHeight * 0.875, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'righttriangle':
            // Right triangle text positioned in lower-left area
            return {
                txtWidth: shapeWidth * 0.5, txtHeight: shapeHeight * 0.5, txtPinX: shapeWidth * 0.25,
                txtPinY: shapeHeight * 0.25, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'triangle':
            // Triangle text positioned in lower-center area
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 0.6667, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.3333, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'pentagon':
            // Pentagon with adjusted vertical pin position
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.4472, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.4472
            };
        case 'heptagon':
            // Heptagon with adjusted vertical pin position
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.474, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.474
            };
        case 'octagon':
        case 'polygon':
        case 'hexagon':
            // Regular polygons with centered text
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'trapezoid':
            // Trapezoid with centered text
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'decagon':
            // Decagon with centered text
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'parallelogram':
            // Parallelogram with centered text
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'cylinder':
            // Cylinder with centered text
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        case 'diamond':
            // Diamond with centered text
            return {
                txtWidth: shapeWidth * 1, txtHeight: shapeHeight * 1, txtPinX: shapeWidth * 0.5,
                txtPinY: shapeHeight * 0.5, txtLocPinX: shapeWidth * 0.5, txtLocPinY: shapeHeight * 0.5
            };
        default:
            // Default: use full shape dimensions with center positioning
            return {
                txtWidth: shapeWidth, txtHeight: shapeHeight, txtPinX: shapeWidth / 2,
                txtPinY: shapeHeight / 2, txtLocPinX: shapeWidth / 2, txtLocPinY: shapeHeight / 2
            };
    }
}
/**
 * Represents a text annotation for Visio connectors (lines, arrows).
 * Extends VisioAnnotation with connector-specific text positioning properties.
 */
export class VisioConnectorAnnotation extends VisioAnnotation {
    /**
     * Creates a VisioConnectorAnnotation instance from a Visio connector shape object.
     * Extracts all text and positioning properties specific to connectors.
     * @param {any} shape - The Visio connector shape object
     * @param {any} defaultData - Default text transform data for fallback values
     * @returns {VisioConnectorAnnotation} A new VisioConnectorAnnotation with extracted properties
     */
    static fromJs(shape, defaultData) {
        const annotation = new VisioConnectorAnnotation();
        // Validate shape properties
        if (!shape || !shape.$ || !shape.Cell) {
            return annotation;
        }
        /**
         * Helper function to get cell value by name
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value
         */
        const getCell = (name) => {
            // Ensure shape.Cell is always an array
            const cells = ensureArray(shape.Cell);
            const cell = cells.find((c) => c.$.N === name);
            return cell ? cell.$.V : undefined;
        };
        // Extract text content
        annotation.content = (shape.Text && shape.Text.value) ? shape.Text.value : '';
        // Extract and convert text rotation angle from radians to degrees
        const txtAngle = parseFloat(getCell('TxtAngle'));
        annotation.rotateAngle = !isNaN(txtAngle) ? txtAngle * (180 / Math.PI) : 0;
        // Extract QuickStyle properties
        annotation.QuickStyleFontColor = getCell('QuickStyleFontColor') !== undefined ? Number(getCell('QuickStyleFontColor')) : undefined;
        annotation.QuickStyleFontMatrix = getCell('QuickStyleFontMatrix') !== undefined ? Number(getCell('QuickStyleFontMatrix')) : undefined;
        // Extract margin and styling
        annotation.margin = VisioMarginModel.fromJs(shape);
        annotation.style = VisioTextStyleModel.fromJs(shape, true);
        // Extract visibility (HideText = '1' means hidden)
        annotation.visible = getCell('HideText') !== '1';
        // Extract hyperlink if present
        annotation.hyperlink = VisioHyperlinkModel.fromJs(shape);
        // Apply constraint flags (locks, selection, rotation)
        getAnnotationConstraints(annotation, shape.Cell);
        // Determine if text follows connector segment angle
        annotation.segmentAngle = getCell('TextDirection') ? getCell('TextDirection') === '1' : false;
        // Set alignment based on text direction
        if (annotation.segmentAngle) {
            // For segmented text, use horizontal alignment
            annotation.horizontalAlignment = getHorizontalAlignment(shape);
        }
        else {
            // For regular text, use vertical alignment
            annotation.verticalAlignment = getVerticalAlignment(shape);
        }
        // Extract text positioning properties with fallback to defaults
        annotation.txtPinX = getCell('TxtPinX') !== undefined ? parseFloat(getCell('TxtPinX')) : defaultData.txtPinX;
        annotation.txtPinY = getCell('TxtPinY') !== undefined ? parseFloat(getCell('TxtPinY')) : defaultData.txtPinY;
        annotation.txtLocPinX = getCell('TxtLocPinX') !== undefined ? parseFloat(getCell('TxtLocPinX')) : defaultData.txtLocalPinX;
        annotation.txtLocPinY = getCell('TxtLocPinY') !== undefined ? parseFloat(getCell('TxtLocPinY')) : defaultData.txtLocalPinY;
        annotation.txtWidth = getCell('TxtWidth') !== undefined ? parseFloat(getCell('TxtWidth')) : defaultData.txtWidth;
        annotation.txtHeight = getCell('TxtHeight') !== undefined ? parseFloat(getCell('TxtHeight')) : defaultData.txtHeight;
        return annotation;
    }
}
/**
 * Represents a text annotation for Visio node shapes (not connectors).
 * Extends VisioAnnotation with node-specific text positioning based on shape type.
 */
export class VisioNodeAnnotation extends VisioAnnotation {
    /**
     * Creates a VisioNodeAnnotation instance from a Visio node shape object.
     * Extracts text properties and applies shape-specific text positioning.
     * @param {any} shape - The Visio node shape object
     * @param {ParsedXmlObject} defaultData - Default data containing shape name, width, height, and positioning
     * @returns {VisioNodeAnnotation} A new VisioNodeAnnotation with extracted properties
     */
    static fromJs(shape, defaultData) {
        const annotation = new VisioNodeAnnotation();
        // Validate shape properties
        if (!shape || !shape.$ || !shape.Cell) {
            return annotation;
        }
        /**
         * Helper function to get cell value by name
         * @param {string} name - The name of the cell to find
         * @returns {string | undefined} The cell value
         */
        const getCell = (name) => {
            // Ensure shape.Cell is always an array
            const cells = ensureArray(shape.Cell);
            const cell = cells.find((c) => c.$.N === name);
            return cell ? cell.$.V : undefined;
        };
        const defaultNodeData = defaultData;
        // Extract text content (normalize bullets/paragraphs for EJ2)
        const rawContent = (shape.Text && shape.Text.value) ? shape.Text.value : '';
        annotation.content = normalizeParagraphBreaks(shape, rawContent);
        // Extract rotation angle and text direction
        const txtAngle = parseFloat(getCell('TxtAngle'));
        const textDirection = Number(getCell('TextDirection'));
        annotation.rotateAngle = !isNaN(txtAngle) ? txtAngle * (180 / Math.PI) : 0;
        // Adjust rotation if text direction is vertical
        if (textDirection === 1) {
            annotation.rotateAngle -= 90;
        }
        // Extract margin and styling
        annotation.margin = VisioMarginModel.fromJs(shape);
        annotation.style = VisioTextStyleModel.fromJs(shape);
        // If the Visio text has mixed formatting runs, fall back to a safe, default style
        if (hasMixedCharacterFormatting(shape) && annotation.style) {
            annotation.style = applyDefaultStyleForMixedRuns(annotation.style);
        }
        // Extract visibility
        annotation.visible = getCell('HideText') !== '1';
        // Extract hyperlink
        annotation.hyperlink = VisioHyperlinkModel.fromJs(shape);
        // Apply constraint flags
        getAnnotationConstraints(annotation, shape.Cell);
        // Determine text direction mode
        annotation.segmentAngle = textDirection ? textDirection === 1 : false;
        // Set alignment based on text direction
        if (annotation.segmentAngle) {
            annotation.horizontalAlignment = getHorizontalAlignment(shape);
        }
        else {
            annotation.verticalAlignment = getVerticalAlignment(shape);
        }
        // Extract shape dimensions with fallback to defaults
        const shapeWidth = !isNaN(parseFloat(getCell('Width')))
            ? parseFloat(getCell('Width'))
            : (defaultNodeData && defaultNodeData.Width) || 1;
        const shapeHeight = !isNaN(parseFloat(getCell('Height')))
            ? parseFloat(getCell('Height'))
            : (defaultNodeData && defaultNodeData.Height) || 1;
        // Get shape-specific text transform properties
        const transform = applyTextTransform(defaultNodeData.Name, shapeWidth, shapeHeight);
        // Extract text width with fallback to shape-specific transform
        const txtWidth = parseFloat(getCell('TxtWidth'));
        annotation.txtWidth = !isNaN(txtWidth) ? txtWidth : transform.txtWidth;
        // Extract text height with fallback to shape-specific transform
        const txtHeight = parseFloat(getCell('TxtHeight'));
        annotation.txtHeight = !isNaN(txtHeight) ? txtHeight : transform.txtHeight;
        // Extract text pin X coordinate with fallback
        const txtPinX = parseFloat(getCell('TxtPinX'));
        annotation.txtPinX = !isNaN(txtPinX) ? txtPinX : transform.txtPinX;
        // Extract text pin Y coordinate with fallback
        const txtPinY = parseFloat(getCell('TxtPinY'));
        annotation.txtPinY = !isNaN(txtPinY) ? txtPinY : transform.txtPinY;
        // Extract text local pin X coordinate with fallback
        const txtLocPinX = parseFloat(getCell('TxtLocPinX'));
        annotation.txtLocPinX = !isNaN(txtLocPinX) ? txtLocPinX : transform.txtLocPinX;
        // Extract text local pin Y coordinate with fallback
        const txtLocPinY = parseFloat(getCell('TxtLocPinY'));
        annotation.txtLocPinY = !isNaN(txtLocPinY) ? txtLocPinY : transform.txtLocPinY;
        return annotation;
    }
    /**
     * Applies a master-text fallback to a node annotation when page text is empty.
     * Keeps page text if present; otherwise fills from master and forces visibility true.
     * @static
     * @param {VisioNodeAnnotation} annotation - The already-parsed page annotation object
     * @param {VisioShapeNode} pageShape - The page-level shape XML (source for page text)
     * @param {VisioShapeNode | null} masterShape - The master-level shape XML (source for placeholder text)
     * @returns {void}
     */
    static applyMasterTextFallback(annotation, pageShape, masterShape) {
        // Guard: invalid annotation or page shape
        if (!annotation) {
            return;
        }
        if (!pageShape) {
            return;
        }
        // -- Check if page text has any visible content --
        let pageText = '';
        if (pageShape.Text && pageShape.Text.value) {
            pageText = String(pageShape.Text.value);
        }
        let hasPageText = false;
        if (pageText && pageText.trim().length > 0) {
            hasPageText = true;
        }
        // -- If page has text, keep it and return --
        if (hasPageText) {
            return;
        }
        // -- Otherwise, try to use master placeholder text --
        if (!masterShape) {
            return;
        }
        let masterTextRaw = '';
        if (masterShape.Text && masterShape.Text.value) {
            masterTextRaw = String(masterShape.Text.value);
        }
        if (!masterTextRaw || masterTextRaw.trim().length === 0) {
            return;
        }
        // -- Normalize master text using the same paragraph rules as page text --
        const normalized = normalizeParagraphBreaks(masterShape, masterTextRaw);
        // -- Apply content and ensure visibility --
        annotation.content = normalized;
        annotation.visible = true;
    }
}
/**
 * Determines horizontal alignment for connector text following segment angle.
 * Maps Visio VerticalAlign cell values to horizontal alignment for rotated text.
 * @function getHorizontalAlignment
 * @param {any} shape - The Visio shape object containing Cell elements
 * @returns {'Left' | 'Center' | 'Right'} The horizontal alignment value
 */
function getHorizontalAlignment(shape) {
    // Find VerticalAlign cell
    const cells = ensureArray(shape.Cell);
    const cell = cells.find((c) => c.$.N === 'VerticalAlign');
    if (cell) {
        // Map alignment codes: 0 = left, 2 = right, default = center
        switch (cell.$.V) {
            case '0':
                return 'Left';
            case '2':
                return 'Right';
            default:
                return 'Center';
        }
    }
    return 'Center';
}
/**
 * Extracts and applies text constraint flags from a shape's cells.
 * Sets constraint properties like text editing, selection, and rotation locks.
 * @function getAnnotationConstraints
 * @param {any} shape - The annotation object to populate with constraint flags
 * @param {VisioCell[]} cells - The array of Cell objects from the shape
 * @returns {void} The modified shape object with constraint flags applied
 */
function getAnnotationConstraints(shape, cells) {
    // Convert cells array to a map for easier lookup
    const cellMap = mapCellValues(cells);
    // Array of lock constraint keys to check
    const lockKeys = [
        'LockTextEdit',
        'LockSelect',
        'LockRotate'
    ];
    // Iterate through lock keys and set boolean flags on shape
    // Convert value to true if it exists and is not '0'
    for (const key of lockKeys) {
        const value = cellMap.get(key);
        shape[toCamelCase(key)] = value != null && value !== '0';
    }
}
/**
 * Determines vertical alignment for node text.
 * Maps Visio VerticalAlign cell values to vertical alignment (Top, Center, Bottom).
 * @function getVerticalAlignment
 * @param {any} shape - The Visio shape object containing Cell elements
 * @returns {'Top' | 'Center' | 'Bottom'} The vertical alignment value
 */
function getVerticalAlignment(shape) {
    // Find VerticalAlign cell
    const cells = ensureArray(shape.Cell);
    const cell = cells.find((c) => c.$.N === 'VerticalAlign');
    if (cell) {
        // Map alignment codes: 0 = bottom, 2 = top, default = center
        switch (cell.$.V) {
            case '0':
                return 'Bottom';
            case '2':
                return 'Top';
            default:
                return 'Center';
        }
    }
    return 'Center';
}
